import { useCallback } from 'react';

import { useToast } from '../../components/Toast.tsx';
import { formatBytes } from '../../lib/display.ts';
import { downloadTo } from '../../lib/download.ts';

/**
 * Downloading, with the user told what is happening.
 *
 * One hook rather than four copies of the same eight lines: the row menu, the
 * viewer's Download button, the viewer's "no viewer for this kind of file"
 * screen and the selection bar were each doing their own fetch-then-click,
 * and none of them said anything between the click and the file appearing.
 *
 * See lib/download.ts for why these are fetched rather than linked, and why
 * that costs the browser's own download indicator.
 */

export interface ArchiveSelection {
  fileIds?: string[];
  folderIds?: string[];
  spreadsheetIds?: string[];
  documentIds?: string[];
}

export interface Downloads {
  /** One file from the Drive, by id. */
  file(id: string, name: string): Promise<void>;
  /** A selection, or a whole folder, as one zip. */
  archive(selection: ArchiveSelection, label?: string): Promise<void>;
}

export function useDownloads(): Downloads {
  const toast = useToast();

  /**
   * The shared half: run it, and narrate.
   *
   * `building` covers the gap an archive has and a plain file does not — the
   * server is walking the selection and compressing before a single byte
   * comes back, and during that the percentage is honestly unknown.
   */
  const run = useCallback(
    async (name: string, building: string, go: (report: Report) => Promise<void>) => {
      const card = toast.progress(`Downloading "${name}"`, building);
      try {
        await go((fraction, loaded, total) => {
          card.update(
            fraction,
            total === null
              ? `${formatBytes(loaded)} so far`
              : `${formatBytes(loaded)} of ${formatBytes(total)}`,
          );
        });
        card.done(`Downloaded "${name}"`, 'Check your browser downloads.');
      } catch (caught) {
        card.fail(
          `Could not download "${name}"`,
          caught instanceof Error ? caught.message : 'Please try again.',
        );
      }
    },
    [toast],
  );

  const file = useCallback(
    (id: string, name: string) =>
      run(name, 'Starting…', (onProgress) =>
        downloadTo(`/api/drive/files/${id}/download`, name, { onProgress }),
      ),
    [run],
  );

  const archive = useCallback(
    (selection: ArchiveSelection, label = 'archive') =>
      run(label, 'Building the archive…', (onProgress) =>
        downloadTo('/api/drive/bulk/download', 'drive-selection.zip', {
          method: 'POST',
          body: selection,
          onProgress,
        }),
      ),
    [run],
  );

  return { file, archive };
}

type Report = (fraction: number | null, loaded: number, total: number | null) => void;
